import { Theme, css } from '@emotion/react';
import { Box, MenuItem } from '@mui/material';
import { Form, Formik } from 'formik';
import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import { RegistrationPolicy } from '@jebel/constants';
import { composeOptionalField, shouldBeEmail, shouldBeURL, shouldBeZipCode } from '@jebel/utils';

import { FormSelectCommunityTypes } from 'shared/components/form';
import {
  Button,
  FormPhoneField,
  FormSelect,
  FormTextField,
  LoadingOverlay,
  RouteLayout,
  Skeleton,
  Typography,
} from 'shared/components/ui';
import { US_PHONE_CODE, US_TIMEZONES_OPTIONS } from 'shared/constants';
import { AddressCreateInput, CommunityTypeFragment } from 'shared/graphql';
import { useCommunityProfile, useToast } from 'shared/hooks';
import { useSchoolConfigurationUpdate } from 'shared/hooks/useSchoolConfigurationUpdate';
import { recordError } from 'shared/utils/record';

const containerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const formCSS = css`
  ${containerCSS};

  max-width: 40rem;
  padding: 2rem;
  gap: 1rem;
`;

const footerCSS = css`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const titleCss = (theme: Theme) => css`
  padding-left: ${theme.spacing(6)}px;
  padding-top: ${theme.spacing(2)}px;
  padding-bottom: ${theme.spacing(2)}px;
  border-bottom: 1px solid ${theme.palette.divider};
`;

const columnsCSS = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

interface FormValues {
  name: string;
  fullName: string;
  type?: CommunityTypeFragment;
  description: string;
  email: string;
  timezone: string;
  website: string;
  phoneNumber: string;
  registrationPolicy: string;
  address?: AddressCreateInput;
}

export function SiteSettings() {
  const { selectedCommunity: community } = useCommunityContext();
  const { showSuccess, showError } = useToast();
  const { mutate: schoolConfigurationUpdate } = useSchoolConfigurationUpdate();

  const { data: profile, loading } = useCommunityProfile({
    skip: !community?.id,
    variables: { id: community?.id as string },
  });

  const initials: FormValues = useMemo(() => {
    const values: FormValues = {
      name: profile?.name ?? '',
      fullName: profile?.fullName ?? '',
      type: profile?.type ?? undefined,
      description: profile?.description ?? '',
      email: profile?.email ?? '',
      timezone: profile?.timezone ?? US_TIMEZONES_OPTIONS[0].value,
      website: profile?.website ?? '',
      phoneNumber: profile?.phoneNumber?.number ?? '',
      registrationPolicy: community?.registrationPolicy ?? RegistrationPolicy.ApprovalRequired,
      address: profile?.address ?? undefined,
    };

    return values;
  }, [profile, community]);

  const onSave = async (values: FormValues) => {
    try {
      await schoolConfigurationUpdate({
        name: values.name,
        fullName: values.fullName,
        type: { connect: { id: values.type?.id } },
        description: values.description,
        email: values.email,
        timezone: values.timezone,
        website: values.website,
        phoneNumber: { code: US_PHONE_CODE, number: values.phoneNumber },
        registrationPolicy: values.registrationPolicy,
        address: {
          [initials.address ? 'update' : 'create']: {
            street1: values.address?.street1,
            city: values.address?.city,
            state: values.address?.state,
            zip: values.address?.zip,
          },
        },
      });

      showSuccess('Settings updated successfully');
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return (
    <RouteLayout css={containerCSS}>
      <Typography css={titleCss} variant="subtitle4" align="inherit" paragraph={false}>
        Site Settings
      </Typography>

      <Formik enableReinitialize onSubmit={onSave} initialValues={initials}>
        {form => (
          <Form css={formCSS} onSubmit={form.handleSubmit}>
            <LoadingOverlay loading={loading} overlay={<Skeleton variant="rect" height="40rem" />}>
              <Box css={columnsCSS}>
                <FormTextField
                  name="name"
                  label="App/Site Name"
                  variant="outlined"
                  placeholder="e.g. My Community"
                  required
                />

                <FormTextField
                  name="fullName"
                  label="Full Site Name"
                  variant="outlined"
                  placeholder="e.g. My Community"
                  required
                />
              </Box>

              <FormSelectCommunityTypes
                name="type"
                label="Community Type"
                variant="outlined"
                placeholder="Community Type"
                helperText="Don't see your community type? Contact support to add it."
                allowUnknown="none"
                required
              />

              <FormTextField
                name="description"
                label="Community About Description"
                variant="outlined"
                placeholder="Describe your community, this will be shown on the home page."
                rows={4}
                multiline
                required
              />

              <FormSelect
                name="registrationPolicy"
                label="Registration Policy"
                variant="outlined"
                helperText="Open: Anyone can join immediately. Approval Required: New members must be approved by an community administrator."
                required
              >
                <MenuItem value={RegistrationPolicy.Open}>Open</MenuItem>
                <MenuItem value={RegistrationPolicy.ApprovalRequired}>Approval Required</MenuItem>
              </FormSelect>

              <Box css={columnsCSS}>
                <FormTextField
                  name="email"
                  label="Community Email Address"
                  placeholder="contact@community.com"
                  variant="outlined"
                  validate={shouldBeEmail}
                  required
                />

                <FormPhoneField name="phoneNumber" label="Contact Phone" />

                <FormTextField
                  name="website"
                  label="Community Website/URL"
                  placeholder="https://community.com"
                  variant="outlined"
                  validate={composeOptionalField(shouldBeURL)}
                />

                <FormSelect name="timezone" variant="outlined" label="Timezone" required>
                  {US_TIMEZONES_OPTIONS.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </FormSelect>
              </Box>

              <Typography variant="body2" color="textSecondary">
                Address
              </Typography>

              <Box css={columnsCSS}>
                <FormTextField name="address.street1" label="Street" placeholder="Street" />

                <FormTextField name="address.city" label="City" placeholder="City" />

                <FormTextField name="address.state" label="State" placeholder="State" />

                <FormTextField
                  name="address.zip"
                  label="Zip Code"
                  placeholder="Zip Code"
                  validate={composeOptionalField(shouldBeZipCode)}
                />
              </Box>
            </LoadingOverlay>

            <Box component="footer" css={footerCSS}>
              <Button
                variant="outlined"
                onClick={form.handleReset}
                size="medium"
                type="reset"
                disabled={loading || form.isSubmitting}
              >
                Cancel
              </Button>

              <Button
                loading={form.isSubmitting}
                disabled={loading}
                disableElevation
                variant="contained"
                color="primary"
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </RouteLayout>
  );
}
